import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RegistrationHeader from './components/RegistrationHeader';
import RegistrationForm from './components/RegistrationForm';
import TrustSignals from './components/TrustSignals';
import Icon from '../../components/AppIcon';
import Footer from '../../components/ui/Footer';
import CustomerHeader from '../../components/ui/CustomerHeader';
import { useAuth } from '../../context/AuthContext';


const UserRegistration = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    setSubmitError('');
    try {
      const result = await register({
        email: formData?.email,
        password: formData?.password,
        firstName: formData?.firstName,
        lastName: formData?.lastName,
      });
      if (!result?.success) {
        setSubmitError(result?.error || 'Registration failed. Please try again.');
        return;
      }
      setRegistrationSuccess(true);
      // AuthContext.register navigates to /user-account-dashboard on success
    } catch (error) {
      console.error('Registration failed:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto">
            <Icon name="CheckCircle" size={32} color="white" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              Welcome to Lalay Fashion!
            </h2>
            <p className="text-muted-foreground">
              Your account has been created successfully. Redirecting you now...
            </p>
          </div>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader
        cartItemCount={0}
        onCartClick={() => {}}
      />

      <div className="pt-20 pb-16 flex-1">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5"></div>
        <div className="relative min-h-screen flex items-center justify-center p-4">
          <div className="w-full max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12">
              
              {/* Left Column - Header & Trust Signals */}
              <div className="space-y-8">
                <RegistrationHeader />
                
                {/* Trust Signals - Hidden on mobile, shown on desktop */}
                <div className="hidden lg:block">
                  <TrustSignals />
                </div>
              </div>

              {/* Right Column - Registration Form */}
              <div className="w-full">
                <div className="bg-card rounded-2xl shadow-elevation-2 border border-border p-6 lg:p-8">
                  {submitError && (
                    <div className="mb-4 p-3 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                      {submitError}
                    </div>
                  )}

                  {/* Registration Form */}
                  <RegistrationForm
                    onSubmit={handleFormSubmit}
                    isLoading={isLoading}
                  />

                  {/* Already have an account? — visible after the form so users
                      who changed their mind can jump to login without scrolling
                      back to the header link. */}
                  <p className="text-center text-sm text-muted-foreground mt-6">
                    Already have an account?{' '}
                    <Link
                      to="/user-login"
                      className="text-primary font-medium hover:underline"
                    >
                      Sign in
                    </Link>
                  </p>

                  {/* Mobile Trust Signals */}
                  <div className="lg:hidden mt-8 pt-6 border-t border-border">
                    <TrustSignals />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UserRegistration;